import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TripStatusPill } from "@/components/StatusPills";
import { STATUS_COLORS, STATUS_LABELS } from "@/lib/status";
import { LogOut, MapPin, DollarSign, User, CheckCircle2, XCircle, Navigation, Wallet, TrendingUp, TrendingDown, Bell, BellOff, Undo2, ShoppingCart, History as HistoryIcon, Banknote, Send, CreditCard, Building2, ChevronDown, ChevronRight, Trophy, Phone, Minus, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DriverScheduleDialog from "@/components/DriverScheduleDialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { playAlertSound, vibrateAlert, requestNotificationPermission, showBrowserNotification } from "@/lib/notify";
import { subscribeToPush, getServiceWorkerRegistration } from "@/lib/push";

const STATUS_MAP = {
  free:    { label: "Disponible", color: "#10B981" },
  busy:    { label: "Ocupado",    color: "#F59E0B" },
  offline: { label: "Offline",    color: "#6B7280" },
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Naples-EDT relative date labels: "Hoy, 31 Jul", "Ayer, 30 Jul", "Mié, 29 Jul"
const NAPLES_TZ = "America/New_York";
// YYYY-MM-DD in Naples-EDT (accounting day). Used both to bucket the daily
// list AND as the `start=` param sent to the backend — the backend recognises
// the date-only form and interprets it as midnight Naples-EDT.
const ymd = (d) => (d instanceof Date ? d : new Date(d)).toLocaleDateString(
  "en-CA", { timeZone: NAPLES_TZ }
);
const naplesDateISO = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return ymd(d);
};
// YYYY-MM-DD of the current accounting-week Friday in Naples-EDT.
// offsetWeeks=0 → most-recent Friday (today if today is Friday).
// offsetWeeks=1 → the Friday one week earlier (start of previous week).
// The accounting week is Fri 00:00 → Thu 23:59 Naples-EDT.
const _NAPLES_DOW = new Intl.DateTimeFormat("en-US", { timeZone: NAPLES_TZ, weekday: "short" });
const _DOW_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const naplesFridayISO = (offsetWeeks = 0) => {
  const now = new Date();
  const dow = _DOW_MAP[_NAPLES_DOW.format(now)] ?? 5;
  const back = ((dow - 5 + 7) % 7) + offsetWeeks * 7;
  const d = new Date(now);
  d.setDate(d.getDate() - back);
  return ymd(d);
};
function friendlyDate(bucket) {
  if (!bucket) return "—";
  const parts = bucket.split("-");
  if (parts.length !== 3) return bucket;
  const dObj = new Date(`${bucket}T12:00:00`);
  if (Number.isNaN(dObj.getTime())) return bucket;
  const monthShort = dObj.toLocaleDateString("es-ES", { month: "short" }).replace(".", "");
  const monthCap = monthShort.charAt(0).toUpperCase() + monthShort.slice(1);
  const dm = `${Number(parts[2])} ${monthCap}`;
  const today = naplesDateISO(0);
  const yest = naplesDateISO(-1);
  if (bucket === today) return `Hoy, ${dm}`;
  if (bucket === yest) return `Ayer, ${dm}`;
  const wd = dObj.toLocaleDateString("es-ES", { weekday: "short" }).replace(".", "");
  const wdCap = wd.charAt(0).toUpperCase() + wd.slice(1);
  return `${wdCap}, ${dm}`;
}

export default function DriverApp() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [trips, setTrips] = useState([]);
  const [driverStatus, setDriverStatus] = useState(user?.driver_status || "offline");
  const [tab, setTab] = useState("viajes");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  // Saved-address aliases (favoritos). Map lowercased real address → friendly
  // name so the driver sees "La Mexicana" instead of "4055 23rd Ave SW…".
  // The "Ir" button still uses the real address / lat-lng — only the label
  // changes.
  const [aliasByAddress, setAliasByAddress] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/aliases");
        if (cancelled) return;
        const map = {};
        (data || []).forEach((a) => {
          const key = (a.address || "").trim().toLowerCase();
          if (key) map[key] = a.name;
        });
        setAliasByAddress(map);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("aliases load failed:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const nameForAddress = useCallback(
    (addr) => aliasByAddress[(addr || "").trim().toLowerCase()] || null,
    [aliasByAddress]
  );
  const knownTripIdsRef = useRef(null); // Map<id,{status,queued}>  (null = first load)
  const knownTripStatusRef = useRef(new Map()); // Map<id,status> — detect revert
  const audioUnlockedRef = useRef(false);
  const alertLoopRef = useRef(null);
  // Prevents overlapping /trips polls when the backend or Cloudflare edge
  // is slow, so we don't stack requests on the same socket pool.
  const loadingRef = useRef(false);
  const wakeLockRef = useRef(null);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  // `undefined` = not tested yet, `true`/`false` = capability of the browser.
  const [wakeLockSupported] = useState(() =>
    typeof navigator !== "undefined" && "wakeLock" in navigator
  );

  // Weekly close reminder — accounting week is Fri 00:00 → Thu 23:59 in Naples-EDT.
  // On Fridays between 00:00–06:00 Naples, remind the driver to hand in cash /
  // upload investment receipts before the new week begins. Shown at most once
  // per Naples-EDT day (persisted via localStorage).
  useEffect(() => {
    const check = () => {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        weekday: "short",
        hour: "2-digit",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour12: false,
      }).formatToParts(new Date());
      const g = (t) => parts.find((p) => p.type === t)?.value;
      if (g("weekday") !== "Fri") return;
      const h = parseInt(g("hour") || "0", 10);
      if (h >= 6) return;
      const key = `weekly-close-reminder-${g("year")}-${g("month")}-${g("day")}`;
      if (localStorage.getItem(key) === "shown") return;
      localStorage.setItem(key, "shown");
      toast.warning(
        "Cierre de semana contable",
        {
          description:
            "Entrega el efectivo pendiente a la empresa y sube las fotos de recibos de inversión antes de arrancar la nueva semana.",
          duration: 60000,
          id: "weekly-close-reminder",
        }
      );
    };
    check();
    const int = setInterval(check, 60_000);
    return () => clearInterval(int);
  }, []);

  // Wake Lock: keep the screen on while the driver is on-shift (free/busy).
  // Automatically released when the driver goes offline, logs out, or the tab
  // loses visibility (browser will re-acquire on visibilitychange).
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let cancelled = false;
    const acquire = async () => {
      try {
        if (wakeLockRef.current) return;
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        setWakeLockActive(true);
        wakeLockRef.current.addEventListener("release", () => {
          wakeLockRef.current = null;
          setWakeLockActive(false);
        });
      } catch (err) {
        // Wake Lock is best-effort: not visible / permissions denied / iOS limitations.
        // Log for debugging but don't surface to user.
        if (typeof console !== "undefined") console.debug("Wake lock acquire skipped:", err?.message || err);
        setWakeLockActive(false);
      }
    };
    const release = async () => {
      if (wakeLockRef.current) {
        try { await wakeLockRef.current.release(); } catch (err) {
          if (typeof console !== "undefined") console.debug("Wake lock release failed:", err?.message || err);
        }
        wakeLockRef.current = null;
      }
      setWakeLockActive(false);
    };
    if (driverStatus !== "offline") acquire();
    else release();
    const onVis = () => {
      if (!cancelled && driverStatus !== "offline" && document.visibilityState === "visible") {
        acquire();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      release();
    };
  }, [driverStatus]);

  // Re-sync driver_status FROM the server when the app returns to foreground.
  // Fixes: driver put the phone in background → came back → app said OFFLINE
  // but the dispatcher panel (and the server) still had them as LIBRE. The
  // discrepancy came from `/auth/me` not returning `driver_status`; now that
  // the backend includes it we also actively re-read on visibilitychange so
  // the toggle reflects server truth after any suspend/resume gap.
  useEffect(() => {
    const resync = async () => {
      try {
        const { data } = await api.get("/auth/me");
        const remote = data?.driver_status;
        // Call setState unconditionally when we get a value — React bails on
        // identical primitives so this is cheap and avoids stale-closure
        // comparisons.
        if (remote) setDriverStatus(remote);
      } catch (err) {
        console.debug("driver_status resync skipped:", err?.message || err);
      }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") resync();
    };
    const onFocus = () => resync();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    // Also run once on mount so the very first render (post-login / hard
    // reload) has the correct state.
    resync();
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const notifyNewTrip = useCallback((trip) => {
    playAlertSound();
    vibrateAlert();
    showBrowserNotification(
      "🚕 Nuevo viaje asignado",
      `${trip.passenger_name || "Pasajero"} · ${trip.pickup_address} → ${trip.dropoff_address} · $${Number(trip.price || 0).toFixed(2)}`
    );
    toast.success(`Nuevo viaje: ${trip.passenger_name || "Pasajero"}`, {
      description: `${trip.pickup_address} → ${trip.dropoff_address}`,
      duration: 8000,
    });
  }, []);

  const load = useCallback(async () => {
    // In-flight guard + skip when tab hidden. Prevents pile-ups on slow
    // backend / Cloudflare hiccups that would otherwise stack polls.
    if (loadingRef.current || document.hidden) return;
    loadingRef.current = true;
    try {
      const { data } = await api.get("/trips");
      // Notification rules (strict):
      // 1. Trip is brand-new to us AND arrives non-queued + assigned  → BEEP
      //    (dispatcher just handed us a fresh trip).
      // 2. Trip we already knew as `queued=true` flips to `queued=false`
      //    AND is `assigned`                                          → BEEP
      //    (the previous trip was closed and this one is now primary).
      // Any other transition (accepted → arrived_pickup, driver taps
      // buttons on the active trip, etc.) must NOT trigger the sound.
      const prev = knownTripIdsRef.current;
      if (prev !== null) {
        data.forEach((t) => {
          if (t.status !== "assigned") return;
          const before = prev.get(t.id);
          const isNew = !before && !t.queued;
          const wasPromoted = before && before.queued === true && !t.queued;
          if (isNew || wasPromoted) notifyNewTrip(t);
        });
      }
      knownTripIdsRef.current = new Map(
        data.map((t) => [t.id, { status: t.status, queued: !!t.queued }])
      );
      // Detect status reverts triggered by the dispatcher: if we knew this
      // trip was already further along and now it moved back one step, tell
      // the driver so they know why buttons "went back".
      const STATUS_ORDER = ["pending", "assigned", "accepted", "arrived_pickup", "with_client", "pending_review", "completed"];
      const prevMap = knownTripStatusRef.current;
      data.forEach((t) => {
        const before = prevMap.get(t.id);
        if (before && before !== t.status) {
          const pi = STATUS_ORDER.indexOf(before);
          const ni = STATUS_ORDER.indexOf(t.status);
          if (pi >= 0 && ni >= 0 && ni < pi) {
            const label = STATUS_LABELS[t.status] || t.status;
            toast.warning("El dispatcher retrocedió el estado", {
              description: `Viaje #${t.trip_number || "sin nº"}: ahora está en "${label}".`,
              duration: 10000,
              id: `revert-${t.id}-${t.status}`,
            });
            try { vibrateAlert(); }
            catch (err) { console.debug("vibrate on revert failed:", err?.message || err); }
          }
        }
      });
      knownTripStatusRef.current = new Map(data.map((t) => [t.id, t.status]));
      setTrips(data);
    } catch (e) {
      // Silent for background polls — Cloudflare/network hiccups shouldn't
      // spam the driver with error toasts while they're driving.
      if (process.env.NODE_ENV !== "production") console.debug("driver poll failed:", e?.message || e);
    } finally {
      loadingRef.current = false;
    }
  }, [notifyNewTrip]);

  useEffect(() => {
    // Auto-register Service Worker on mount so it's ready before user grants permission
    getServiceWorkerRegistration();
    load();
    const int = setInterval(load, 5000);
    return () => clearInterval(int);
  }, [load]);

  // If permission is already granted (previous session), silently re-subscribe
  useEffect(() => {
    if (notifPerm === "granted") {
      subscribeToPush().catch(() => {});
    }
  }, [notifPerm]);

  // Continuous alert loop: sound + vibration every 2s while there is any
  // ACTIVE unaccepted trip (status='assigned' AND NOT queued). Queued trips
  // are pre-assigned and must stay silent — the driver only hears them when
  // the previous trip closes and this one is promoted to primary.
  const hasUnaccepted = trips.some((t) => t.status === "assigned" && !t.queued);
  useEffect(() => {
    if (!hasUnaccepted) {
      if (alertLoopRef.current) {
        clearInterval(alertLoopRef.current);
        alertLoopRef.current = null;
      }
      return;
    }
    // Fire once immediately, then keep repeating every 2s (matches siren length)
    playAlertSound();
    vibrateAlert();
    alertLoopRef.current = setInterval(() => {
      playAlertSound();
      vibrateAlert();
    }, 2000);
    return () => {
      if (alertLoopRef.current) {
        clearInterval(alertLoopRef.current);
        alertLoopRef.current = null;
      }
    };
  }, [hasUnaccepted]);

  const enableNotifications = async () => {
    // Unlock audio (browsers require user gesture) and request notification permission
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0.0001;
        osc.connect(gain).connect(ctx.destination);
        osc.start(); osc.stop(ctx.currentTime + 0.01);
      }
      audioUnlockedRef.current = true;
    } catch (err) {
      console.error("Audio unlock failed:", err);
    }
    const res = await requestNotificationPermission();
    setNotifPerm(res);
    if (res === "granted") {
      toast.success("Notificaciones activadas");
      // Subscribe to Web Push for background delivery
      const push = await subscribeToPush();
      if (push.ok) {
        toast.success("Push notifications en segundo plano activadas");
      } else if (push.reason && push.reason !== "unsupported") {
        console.warn("Push subscribe failed:", push.reason);
      }
      // Quick test sound so the driver knows it works
      setTimeout(() => playAlertSound(), 200);
    } else if (res === "denied") {
      toast.error("Permiso denegado. Actívalo en la configuración del navegador.");
    } else if (res === "unsupported") {
      toast.info("Tu navegador no soporta notificaciones");
    }
  };

  useEffect(() => {
    if (!navigator.geolocation) return;
    let watchId = null;
    const push = (pos) => {
      api.post("/driver/location", {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      }).catch(() => {});
    };
    if (driverStatus !== "offline") {
      watchId = navigator.geolocation.watchPosition(push, () => {}, {
        enableHighAccuracy: true,
        maximumAge: 15000,
        timeout: 20000,
      });
    }
    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, [driverStatus]);

  const setStatus = async (s) => {
    try {
      await api.post("/driver/status", { driver_status: s });
      setDriverStatus(s);
      toast.success(`Estado: ${STATUS_MAP[s].label}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Track in-flight transitions per trip so a jittery mobile double-tap
  // doesn't fire the same POST /status twice. The backend also dedupes on
  // identical states, but stopping the second request client-side saves a
  // round-trip and keeps the UI/toast quiet.
  const changingRef = useRef(new Set());
  const changeTrip = async (trip, status) => {
    const key = `${trip.id}:${status}`;
    if (changingRef.current.has(key)) return;
    // Also skip if the trip is already in that exact state (stale card).
    if (trip.status === status) return;
    changingRef.current.add(key);
    try {
      await api.post(`/trips/${trip.id}/status`, { status });
      toast.success("Estado actualizado");
      load();
      if (status === "with_client") setDriverStatus("busy");
      if (status === "completed" || status === "cancelled") setDriverStatus("free");
    } catch (e) {
      // 409 = dispatcher already closed/cancelled this trip. Show a clear
      // message and force a refresh so the stale card disappears from the
      // driver's screen instead of allowing more taps that revive the trip.
      if (e?.response?.status === 409) {
        toast.error("El dispatcher ya cerró este viaje. Actualizando…");
        load();
        return;
      }
      toast.error(formatApiError(e));
    } finally {
      changingRef.current.delete(key);
    }
  };

  const releaseTrip = async (trip) => {
    if (!window.confirm("¿Devolver este viaje a dispatch para que otro chofer lo tome?")) return;
    try {
      await api.post(`/trips/${trip.id}/release`);
      toast.success("Viaje liberado — dispatch podrá reasignarlo");
      setDriverStatus("free");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Queued trips: pre-assigned by the dispatcher and held until the current
  // one is completed & approved. Only count trips that are STILL waiting to
  // start (status === "assigned"). Once a queued trip is completed/cancelled
  // its `queued` flag is never cleared server-side, so we must filter by
  // status here or the badge would show stale counts forever.
  const queuedTrips = trips.filter((t) => t.queued && t.status === "assigned");
  const queuedCount = queuedTrips.length;

  const activeTrip = trips.find((t) => !t.queued && ["assigned", "accepted", "arrived_pickup", "with_client", "pending_review"].includes(t.status));
  // Only show additional "assigned" trips if there's NO current one. Queued
  // trips never appear in this list — they only surface once the previous
  // one is closed and the backend un-queues them.
  const pending = activeTrip ? [] : trips.filter((t) => !t.queued && t.status === "assigned");
  // "Historial reciente" del chofer: solo viajes cerrados HOY (día contable
  // Naples). Se reinicia automáticamente al pasar la medianoche EDT/EST.
  // Usamos `completed_at` (congelado al cierre) para que una edición
  // posterior no mueva un viaje de ayer al panel de hoy.
  const _todayYMD = naplesDateISO(0);
  const history = trips
    .filter((t) => {
      if (!["completed", "cancelled"].includes(t.status)) return false;
      const ts = t.completed_at || t.updated_at;
      return ts && ymd(ts) === _todayYMD;
    })
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-background text-foreground max-w-2xl mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-card/95 backdrop-blur-xl border-b border-border">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { refresh?.(); setScheduleOpen(true); }}
              data-testid="driver-logo-schedule-btn"
              title="Ver mi horario"
              className="w-10 h-10 rounded-md bg-white/95 grid place-items-center shadow-[0_0_15px_rgba(251,191,36,0.35)] p-0.5 active:scale-95 transition-transform"
            >
              <img src="/logo.webp" alt="Mi Taxi Naples" className="w-full h-full object-contain" />
            </button>
            <div>
              <div className="text-[10px] font-mono text-primary tracking-widest">MI TAXI · NAPLES</div>
              <div className="font-black text-base" style={{ fontFamily: "Manrope" }}>Hola, {user?.name?.split(" ")[0]}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={async () => { await logout(); nav("/login"); }} data-testid="driver-logout" className="border-border">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        {/* Notification permission banner */}
        {notifPerm !== "granted" && notifPerm !== "unsupported" && (
          <div className="mx-3 mb-2 p-3 rounded-md border border-primary/40 bg-primary/10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <BellOff className="w-4 h-4 text-primary flex-shrink-0" />
              <div className="text-xs">
                <div className="font-bold text-white">Activa notificaciones</div>
                <div className="text-muted-foreground">Recibe alertas y sonido cuando entre un nuevo viaje.</div>
              </div>
            </div>
            <Button
              size="sm"
              onClick={enableNotifications}
              data-testid="enable-notifications-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold flex-shrink-0"
            >
              <Bell className="w-3.5 h-3.5 mr-1" /> Activar
            </Button>
          </div>
        )}
        {notifPerm === "granted" && (
          <div className="mx-3 mb-2 flex items-center justify-between gap-2 text-[10px] font-mono text-emerald-400 uppercase tracking-widest">
            <div className="flex items-center gap-1.5"><Bell className="w-3 h-3" /> Alertas activadas</div>
            <button
              onClick={() => { playAlertSound(); vibrateAlert(); toast.info("Sonido de prueba"); }}
              data-testid="test-sound-btn"
              className="text-muted-foreground hover:text-white underline"
            >
              probar sonido
            </button>
          </div>
        )}
        {/* Status toggle: Disponible <-> Offline. `busy` is set automatically
            by trip transitions, not exposed as a manual button anymore. */}
        <div className="flex items-center justify-between p-3 pt-0 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${driverStatus === "offline" ? "bg-muted-foreground" : driverStatus === "busy" ? "bg-amber-400 animate-pulse" : "bg-emerald-400 animate-pulse"}`}
              aria-hidden
            />
            <span className="text-sm font-bold uppercase tracking-wide" data-testid="driver-status-label">
              {driverStatus === "offline" ? "Offline" : driverStatus === "busy" ? "En viaje" : "Disponible"}
            </span>
            {driverStatus !== "offline" && (
              wakeLockSupported ? (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest ${wakeLockActive ? "text-emerald-400" : "text-amber-400"}`}
                  title={wakeLockActive ? "La pantalla se mantiene encendida mientras estás disponible." : "Pantalla se apagará normalmente. Volveremos a intentarlo al recuperar el foco."}
                  data-testid="wake-lock-indicator"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${wakeLockActive ? "bg-emerald-400" : "bg-amber-400"}`} aria-hidden />
                  {wakeLockActive ? "pantalla activa" : "reintentando"}
                </span>
              ) : (
                <span
                  className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
                  title="Tu navegador no soporta mantener la pantalla encendida. Desactiva el bloqueo automático en Ajustes."
                  data-testid="wake-lock-unsupported"
                >
                  · desactiva auto-bloqueo
                </span>
              )
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={driverStatus !== "offline"}
            onClick={() => setStatus(driverStatus === "offline" ? "free" : "offline")}
            data-testid="driver-availability-switch"
            disabled={driverStatus === "busy"}
            title={driverStatus === "busy" ? "En viaje activo — no se puede desconectar" : ""}
            className={`relative inline-flex h-8 w-16 flex-shrink-0 items-center rounded-full border-2 transition-colors ${
              driverStatus === "offline"
                ? "bg-muted border-muted"
                : "bg-emerald-500 border-emerald-500"
            } ${driverStatus === "busy" ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                driverStatus === "offline" ? "translate-x-1" : "translate-x-8"
              }`}
            />
          </button>
        </div>
        {/* Tabs */}
        <div className="px-3 pb-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full bg-input">
              <TabsTrigger value="viajes" data-testid="driver-tab-viajes">Viajes</TabsTrigger>
              <TabsTrigger value="ingresos" data-testid="driver-tab-ingresos">Mis Ingresos</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </header>

      <DriverScheduleDialog open={scheduleOpen} onOpenChange={setScheduleOpen} schedule={user?.schedule} driverName={user?.name} />

      <div className="p-4 pb-24">
        {tab === "viajes" ? (
          <div className="space-y-6">
            {queuedCount > 0 && (
              <div
                data-testid="driver-queue-badge"
                className="relative flex items-center justify-between gap-3 rounded-lg border-2 border-red-500 bg-red-600/20 px-4 py-3 shadow-[0_0_20px_rgba(239,68,68,0.45)] animate-pulse"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="grid place-items-center w-10 h-10 rounded-full bg-red-600 text-white font-black text-lg shadow-lg shrink-0"
                    aria-hidden
                  >
                    {queuedCount}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-red-200">
                      En cola
                    </div>
                    <div className="text-base font-black text-white leading-tight">
                      {queuedCount === 1
                        ? "Tenés 1 viaje esperando"
                        : `Tenés ${queuedCount} viajes esperando`}
                    </div>
                  </div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-red-200 text-right shrink-0">
                  Se activará al<br />cerrar el actual
                </span>
              </div>
            )}
            {/* Active trip */}
            {activeTrip && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono text-primary tracking-widest">VIAJE ACTUAL</div>
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(true)}
                    data-testid="driver-open-history-btn"
                    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-white transition-colors border border-border rounded-full px-2 py-1"
                  >
                    <HistoryIcon className="w-3 h-3" /> Historial ({history.length})
                  </button>
                </div>
                <BigTripCard trip={activeTrip} onChange={changeTrip} onRelease={releaseTrip} isActive nameForAddress={nameForAddress} />
              </section>
            )}

            {/* Pending assignments — hidden when there's an active trip */}
            {!activeTrip && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono text-muted-foreground tracking-widest">
                    NUEVOS ASIGNADOS ({pending.length})
                  </div>
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(true)}
                    data-testid="driver-open-history-btn"
                    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-white transition-colors border border-border rounded-full px-2 py-1"
                  >
                    <HistoryIcon className="w-3 h-3" /> Historial ({history.length})
                  </button>
                </div>
                {pending.length === 0 ? (
                  <div className="text-center text-muted-foreground text-sm py-8 border border-dashed border-border rounded-md">
                    Sin viajes asignados
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pending.map((t) => <BigTripCard key={t.id} trip={t} onChange={changeTrip} onRelease={releaseTrip} nameForAddress={nameForAddress} />)}
                  </div>
                )}
              </section>
            )}
          </div>
        ) : (
          <MyIncome pct={user?.driver_pct ?? 70} />
        )}
      </div>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="driver-history-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }} className="text-xl flex items-center gap-2">
              <HistoryIcon className="w-5 h-5 text-primary" /> Historial reciente
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto space-y-2">
            {history.length === 0 && (
              <div className="text-center text-muted-foreground text-sm py-8">
                Aún no tienes viajes completados.
              </div>
            )}
            {history.map((t) => (
              <Card key={t.id} className="bg-secondary/30 border-border p-3 flex justify-between items-center" data-testid={`driver-history-item-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <TripStatusPill status={t.status} />
                    {t.trip_type === "delivery" && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] font-bold border border-accent/60 text-accent bg-accent/10">
                        <ShoppingCart className="w-3 h-3" /> DELIVERY
                      </span>
                    )}
                    {t.trip_number && (
                      <span className="text-[10px] font-mono text-muted-foreground">{t.trip_number}</span>
                    )}
                  </div>
                  <div className="text-sm mt-1 truncate">
                    {t.trip_type === "delivery"
                      ? (t.items || t.dropoff_address || "Delivery")
                      : `${t.pickup_address || "—"} → ${t.dropoff_address || "—"}`}
                  </div>
                  {t.trip_type === "delivery" && t.dropoff_address && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      <MapPin className="w-3 h-3 inline text-accent" /> {t.dropoff_address}
                    </div>
                  )}
                </div>
                <div className="font-mono font-bold text-amber-400 ml-3 flex-shrink-0">{fmt(t.price)}</div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating "Llamar a la oficina" — always accessible for drivers on
          the road. `tel:` opens the native dialer with the office number. */}
      <a
        href="tel:+12398325580"
        data-testid="driver-call-office-fab"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 min-w-[220px] flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500 text-white font-bold shadow-[0_10px_25px_rgba(16,185,129,0.35)] active:scale-95 transition-transform"
      >
        <Phone className="w-4 h-4" />
        <span className="text-sm tracking-wide">Llamar a la oficina</span>
      </a>
    </div>
  );
}

function MyIncome({ pct }) {
  const [period, setPeriod] = useState("today");
  const [today, setToday] = useState(null);
  const [yesterday, setYesterday] = useState(null);
  const [week, setWeek] = useState(null);
  const [prevWeek, setPrevWeek] = useState(null);
  const [month, setMonth] = useState(null);
  const [byDay, setByDay] = useState([]);
  const [prevWeekAvg, setPrevWeekAvg] = useState(0);

  const load = useCallback(async () => {
    // Date maths in Naples-EDT so "today"/"this week" line up with the
    // accounting day. We send the backend the date-only form (YYYY-MM-DD) —
    // it interprets those as midnight Naples-EDT.
    const todayYMD = naplesDateISO(0);
    const yesterdayYMD = naplesDateISO(-1);
    // Accounting week = Fri 00:00 → Thu 23:59 (Naples). "This week" starts on
    // the most-recent Friday (or today if today IS Friday). "Previous week"
    // is exactly the seven days before that Friday.
    const weekStartYMD = naplesFridayISO(0);
    const prevWeekStartYMD = naplesFridayISO(1);
    const prevWeekEndYMD = weekStartYMD; // exclusive
    const firstOfMonth = (() => {
      const [y, m] = todayYMD.split("-");
      return `${y}-${m}-01`;
    })();
    const nowIso = new Date().toISOString();

    try {
      const [t, y, w, m, wDaily, wPrev] = await Promise.all([
        api.get(`/reports?grouping=driver&start=${todayYMD}&end=${nowIso}`),
        api.get(`/reports?grouping=driver&start=${yesterdayYMD}&end=${todayYMD}`),
        api.get(`/reports?grouping=driver&start=${weekStartYMD}&end=${nowIso}`),
        api.get(`/reports?grouping=driver&start=${firstOfMonth}&end=${nowIso}`),
        api.get(`/reports?grouping=day&start=${weekStartYMD}&end=${nowIso}`),
        api.get(`/reports?grouping=driver&start=${prevWeekStartYMD}&end=${prevWeekEndYMD}`),
      ]);
      setToday(t.data.total);
      setYesterday(y.data.total);
      setWeek(w.data.total);
      setMonth(m.data.total);
      setByDay(wDaily.data.rows);
      setPrevWeek(wPrev.data.total);
      const prevTotal = Number(wPrev.data?.total?.driver_income || 0);
      setPrevWeekAvg(prevTotal / 7);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); const int = setInterval(load, 15000); return () => clearInterval(int); }, [load]);

  const active = period.startsWith("day:")
    ? (byDay.find((d) => d.bucket === period.slice(4)) || null)
    : { today, yesterday, week, prev_week: prevWeek, month }[period];

  // Short day label: "Vie 14 Ago", "Sáb 15 Ago" (Naples-EDT).
  const dayLabel = (ymdStr) => {
    if (!ymdStr) return "—";
    const dObj = new Date(`${ymdStr}T12:00:00`);
    if (Number.isNaN(dObj.getTime())) return ymdStr;
    const wd = dObj.toLocaleDateString("es-ES", { weekday: "short", timeZone: NAPLES_TZ }).replace(".", "");
    const mo = dObj.toLocaleDateString("es-ES", { month: "short", timeZone: NAPLES_TZ }).replace(".", "");
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return `${cap(wd)} ${Number(ymdStr.split("-")[2])} ${cap(mo)}`;
  };

  const periodLabel = period.startsWith("day:")
    ? dayLabel(period.slice(4))
    : {
        today: "hoy",
        yesterday: "ayer",
        week: "esta semana",
        prev_week: "semana anterior",
        month: "este mes",
      }[period];

  // Comparison against the previous comparable period. Only shown for
  // periods where the comparison is meaningful (today vs yesterday, this
  // week vs previous week). Returns null when there's not enough data.
  const compareRef = period === "today" ? yesterday : period === "week" ? prevWeek : null;
  const compareLabel = period === "today" ? "ayer" : period === "week" ? "semana anterior" : "";
  const compare = (() => {
    if (!compareRef || !active) return null;
    const cur = Number(active.driver_income || 0);
    const prev = Number(compareRef.driver_income || 0);
    if (prev <= 0 && cur <= 0) return null;
    if (prev <= 0) return { deltaPct: null, dir: "up", label: "nuevo", tone: "text-emerald-400", Icon: TrendingUp };
    const pctDelta = ((cur - prev) / prev) * 100;
    const rounded = Math.round(pctDelta);
    if (Math.abs(rounded) < 1) {
      return { deltaPct: 0, dir: "flat", label: "0%", tone: "text-muted-foreground", Icon: Minus };
    }
    return {
      deltaPct: rounded,
      dir: rounded > 0 ? "up" : "down",
      label: `${rounded > 0 ? "+" : ""}${rounded}%`,
      tone: rounded > 0 ? "text-emerald-400" : "text-red-400",
      Icon: rounded > 0 ? TrendingUp : TrendingDown,
    };
  })();

  return (
    <div className="space-y-4">
      {/* Period selector — small dropdown so "hoy" leads and other windows
          stay one tap away without eating vertical space on mobile. */}
      <div className="flex items-center justify-end gap-2">
        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Período</label>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          data-testid="income-period-select"
          className="bg-input border border-border rounded-md text-xs font-bold text-white px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="today">Hoy</option>
          <option value="yesterday">Ayer</option>
          <option value="week">Esta semana (acumulado)</option>
          <option value="prev_week">Semana anterior</option>
          <option value="month">Este mes</option>
          {byDay.length > 0 && (
            <optgroup label="Días de esta semana">
              {byDay
                .slice()
                .sort((a, b) => (a.bucket < b.bucket ? -1 : 1))
                .map((d) => (
                  <option key={d.bucket} value={`day:${d.bucket}`} data-testid={`income-period-day-${d.bucket}`}>
                    {dayLabel(d.bucket)}
                  </option>
                ))}
            </optgroup>
          )}
        </select>
      </div>

      {/* Main earnings card */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/40 p-6 relative overflow-hidden">
        <div className="absolute top-4 right-4">
          <TrendingUp className="w-6 h-6 text-emerald-400 opacity-40" />
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/80 mb-1">
          Tu ingreso · {periodLabel}
        </div>
        <div className="text-5xl font-black font-mono text-emerald-400" data-testid="my-driver-income">
          {fmt(active?.driver_income)}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold"
            data-testid="my-trips-pill"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {active?.trips ?? 0} viaje{(active?.trips ?? 0) !== 1 ? "s" : ""} completado{(active?.trips ?? 0) !== 1 ? "s" : ""}
          </span>
          {compare && (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/30 border border-white/10 ${compare.tone} text-xs font-bold`}
              data-testid="my-vs-previous"
              title={`Comparado con ${compareLabel}`}
            >
              <compare.Icon className="w-3.5 h-3.5" />
              {compare.label}
              <span className="text-muted-foreground font-normal ml-0.5">vs {compareLabel}</span>
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            De <span className="font-mono text-white">{fmt(active?.total_income)}</span> facturado · comisión <span className="font-mono text-white">{pct}%</span>
          </span>
        </div>
      </Card>

      {/* Sub metrics */}
      <div className="grid grid-cols-2 gap-3">
        <MiniStat label="Cobrado en efectivo" value={fmt(active?.driver_collected)} icon={Banknote} iconTint="text-emerald-400" />
        <MiniStat label="Zelle recibido" value={fmt(active?.zelle)} icon={Send} iconTint="text-violet-400" />
        <MiniStat label="Inversión propia" value={fmt(active?.driver_investment)} icon={Wallet} iconTint="text-cyan-400" accent="text-cyan-300" />
        {(() => {
          // Global surplus first: if the driver has handed over more than
          // his lifetime debt (post-cerrar-libros), display it as credit
          // even in a period with no debt. Falls back to the period-scoped
          // net (`outstanding_debt`) when there's no global surplus.
          const globalCredit = Number(active?.credit_balance || 0);
          const periodNet = Number(active?.outstanding_debt ?? active?.driver_to_llc ?? 0);
          const owed = globalCredit > 0.005 ? -globalCredit : periodNet;
          const llcOwesDriver = owed < -0.005;
          return (
            <MiniStat
              label={llcOwesDriver ? "La empresa te debe" : "A entregar a la empresa"}
              value={fmt(Math.abs(owed))}
              icon={Building2}
              iconTint={llcOwesDriver ? "text-emerald-400" : "text-primary"}
              accent={llcOwesDriver ? "text-emerald-400" : "text-primary"}
              hint={llcOwesDriver
                ? "Saldo a tu favor · te lo entregarán en la próxima liquidación"
                : "Efectivo que debes entregar a la empresa en la próxima liquidación"}
              testId="my-owed-to-llc"
            />
          );
        })()}
      </div>

      {/* Daily breakdown */}
      {byDay.length > 0 && (
        <Card className="bg-card border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono text-muted-foreground tracking-widest">DESGLOSE DIARIO · SEMANA CONTABLE</div>
            <DollarSign className="w-4 h-4 text-primary" />
          </div>
          <WeeklyMiniChart days={byDay} prevAvg={prevWeekAvg} />
          <DailyBreakdown days={byDay.slice().reverse()} />
        </Card>
      )}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, accent = "text-white", hint, iconTint = "text-muted-foreground", testId }) {
  return (
    <Card className="bg-card border-border p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${iconTint}`} />}
      </div>
      <div className={`text-xl font-black font-mono ${accent}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{hint}</div>}
    </Card>
  );
}

function WeeklyMiniChart({ days, prevAvg }) {
  if (!days || days.length === 0) return null;
  const H = 72; // bar area height (px)
  const values = days.map((d) => Number(d.driver_income || 0));
  const max = Math.max(1, prevAvg || 0, ...values);
  const linePct = prevAvg > 0.005 ? Math.min(100, (prevAvg / max) * 100) : 0;
  return (
    <div
      className="mb-3 rounded-lg border border-border/60 bg-secondary/20 p-3"
      data-testid="weekly-mini-chart"
    >
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Comparativa semana
        </div>
        {prevAvg > 0.005 && (
          <div
            className="flex items-center gap-1.5 text-[10px] text-muted-foreground"
            data-testid="prev-week-avg-legend"
          >
            <span className="inline-block w-5 border-t-2 border-dashed border-amber-400/80" />
            <span>Promedio semana pasada</span>
            <span className="font-mono text-amber-300">{fmt(prevAvg)}</span>
          </div>
        )}
      </div>
      <div
        className="relative flex items-end gap-1.5"
        style={{ height: `${H}px` }}
        data-testid="weekly-mini-chart-bars"
      >
        {days.map((d) => {
          const v = Number(d.driver_income || 0);
          const h = v > 0 ? Math.max(3, Math.round((v / max) * H)) : 2;
          const isEmpty = v <= 0.005;
          return (
            <div
              key={d.bucket}
              className="flex-1 flex items-end min-w-0"
              title={`${friendlyDate(d.bucket)}: ${fmt(v)}`}
              data-testid={`chart-bar-${d.bucket}`}
            >
              <div
                className={`w-full rounded-t transition-colors ${
                  isEmpty ? "bg-secondary/60" : "bg-emerald-500/70 hover:bg-emerald-400"
                }`}
                style={{ height: `${h}px` }}
              />
            </div>
          );
        })}
        {prevAvg > 0.005 && (
          <div
            aria-hidden
            className="absolute inset-x-0 border-t-2 border-dashed border-amber-400/80 pointer-events-none"
            style={{ bottom: `${linePct}%` }}
            data-testid="prev-week-avg-line"
          />
        )}
      </div>
      <div className="flex gap-1.5 mt-1.5">
        {days.map((d) => {
          const parts = d.bucket.split("-");
          const dd = parts.length === 3 ? Number(parts[2]) : "";
          return (
            <div
              key={d.bucket}
              className="flex-1 text-center text-[9px] font-mono text-muted-foreground truncate"
            >
              {dd}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DailyBreakdown({ days }) {
  const [openDay, setOpenDay] = useState(null);
  const [tripsByDay, setTripsByDay] = useState({});
  const [loadingDay, setLoadingDay] = useState(null);
  const maxIncome = Math.max(1, ...days.map((d) => Number(d.driver_income || 0)));
  const bestDay = days.reduce(
    (a, b) => (Number(b.driver_income || 0) > Number(a?.driver_income || 0) ? b : a),
    null
  );

  const toggle = async (bucket) => {
    if (openDay === bucket) {
      setOpenDay(null);
      return;
    }
    setOpenDay(bucket);
    if (!tripsByDay[bucket]) {
      setLoadingDay(bucket);
      try {
        // Backend interprets `bucket` (YYYY-MM-DD) as midnight..23:59:59 Naples-EDT
        const { data } = await api.get(
          `/trips?status=completed&start=${bucket}&end=${bucket}&limit=200`
        );
        setTripsByDay((s) => ({ ...s, [bucket]: Array.isArray(data) ? data : [] }));
      } catch (e) {
        toast.error(formatApiError(e));
        setTripsByDay((s) => ({ ...s, [bucket]: [] }));
      } finally {
        setLoadingDay(null);
      }
    }
  };

  return (
    <div className="space-y-2" data-testid="daily-breakdown-list">
      {days.map((r) => {
        const income = Number(r.driver_income || 0);
        const pctBar = Math.min(100, Math.round((income / maxIncome) * 100));
        const isOpen = openDay === r.bucket;
        const isBest = bestDay && r.bucket === bestDay.bucket && income > 0.005;
        return (
          <div
            key={r.bucket}
            className="rounded-lg border border-border/60 bg-secondary/20 overflow-hidden"
            data-testid={`day-row-${r.bucket}`}
          >
            <button
              type="button"
              onClick={() => toggle(r.bucket)}
              className="w-full text-left relative p-3 hover:bg-secondary/40 transition-colors"
              data-testid={`day-toggle-${r.bucket}`}
              aria-expanded={isOpen}
            >
              {/* Productivity progress bar (behind content) */}
              <div
                className="absolute inset-y-0 left-0 bg-emerald-500/10 pointer-events-none"
                style={{ width: `${pctBar}%` }}
                aria-hidden
              />
              <div className="relative flex items-center gap-2">
                {isOpen
                  ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold">{friendlyDate(r.bucket)}</span>
                    {isBest && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 font-bold">
                        <Trophy className="w-3 h-3" /> MEJOR DÍA
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {r.trips} viaje{r.trips !== 1 ? "s" : ""} · facturado {fmt(r.total_income)}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-mono font-bold text-emerald-400 text-base">{fmt(income)}</div>
                </div>
              </div>
            </button>
            {isOpen && (
              <div
                className="border-t border-border/40 bg-background/40 divide-y divide-border/30"
                data-testid={`day-detail-${r.bucket}`}
              >
                {loadingDay === r.bucket && (
                  <div className="p-3 text-xs text-muted-foreground">Cargando viajes…</div>
                )}
                {loadingDay !== r.bucket && (tripsByDay[r.bucket] || []).length === 0 && (
                  <div className="p-3 text-xs text-muted-foreground">
                    Sin viajes completados en este día.
                  </div>
                )}
                {(tripsByDay[r.bucket] || []).map((t) => (
                  <DayTripRow key={t.id} trip={t} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DayTripRow({ trip }) {
  const pm = (trip.payment_method || "cash").toLowerCase();
  const PmIcon = pm === "zelle"
    ? Send
    : pm === "cash"
      ? Banknote
      : pm === "card"
        ? CreditCard
        : DollarSign;
  const pmTint = pm === "zelle"
    ? "text-violet-400"
    : pm === "cash"
      ? "text-emerald-400"
      : pm === "card"
        ? "text-sky-400"
        : "text-muted-foreground";
  const pmLabel = pm === "zelle" ? "Zelle" : pm === "cash" ? "Efectivo" : pm === "card" ? "Tarjeta" : "Crédito";
  const _closedAt = trip.completed_at || trip.updated_at;
  const time = _closedAt
    ? new Date(_closedAt).toLocaleTimeString("es-ES", {
        timeZone: "America/New_York",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "";
  const isDelivery = trip.trip_type === "delivery";
  return (
    <div className="flex items-start gap-2 p-3 text-xs" data-testid={`day-trip-${trip.id}`}>
      <div className="flex flex-col items-center gap-0.5 flex-shrink-0 w-14">
        <PmIcon className={`w-4 h-4 ${pmTint}`} />
        <span className={`text-[9px] font-mono uppercase tracking-wider ${pmTint}`}>{pmLabel}</span>
        <span className="font-mono text-[10px] text-muted-foreground">{time}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] text-muted-foreground">{trip.trip_number || "—"}</div>
        {isDelivery ? (
          <div className="text-sm truncate">{trip.items || trip.dropoff_address || "Delivery"}</div>
        ) : (
          <div className="text-sm truncate">
            {trip.pickup_address || "—"} <span className="text-muted-foreground">→</span> {trip.dropoff_address || "—"}
          </div>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-mono font-bold text-amber-400">{fmt(trip.price)}</div>
        <div className="font-mono text-[10px] text-emerald-400">+{fmt(trip.driver_income)}</div>
      </div>
    </div>
  );
}

function BigTripCard({ trip, onChange, onRelease, isActive, nameForAddress }) {
  const pickupAlias = nameForAddress ? nameForAddress(trip.pickup_address) : null;
  const dropoffAlias = nameForAddress ? nameForAddress(trip.dropoff_address) : null;
  // Trim the visible address to end at the state (e.g. "…, Naples, FL"),
  // dropping the ZIP and country tail Google Places tacks on. Keeps the card
  // readable on small phones while `Ir` still navigates using the full string
  // (or lat/lng, when present).
  const shortAddress = (addr) => {
    if (!addr) return "";
    const parts = addr.split(",").map((s) => s.trim()).filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i--) {
      // A "State" chunk is a two-letter uppercase code — optionally followed by
      // a ZIP. Cut anything after that chunk (removes " 34116, EE. UU.").
      const m = parts[i].match(/^([A-Z]{2})(\s+\d{5}(-\d{4})?)?$/);
      if (m) {
        parts[i] = m[1];
        return parts.slice(0, i + 1).join(", ");
      }
    }
    return addr;
  };
  const pickupShort = shortAddress(trip.pickup_address);
  const dropoffShort = shortAddress(trip.dropoff_address);
  const color = STATUS_COLORS[trip.status];
  // Before the driver accepts, hide sensitive info (addresses, price,
  // passenger, products). Dispatchers want the driver to commit based on
  // "hay un viaje" only — no cherry-picking by destination or payout.
  const isUnaccepted = trip.status === "assigned";

  if (isUnaccepted) {
    return (
      <Card
        className="bg-card border-l-4 p-6 space-y-4"
        style={{ borderLeftColor: color }}
        data-testid={`driver-trip-${trip.id}`}
      >
        <div className="flex flex-col items-center text-center gap-2">
          <TripStatusPill status={trip.status} />
          {trip.trip_type === "delivery" && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[11px] font-bold border border-accent/60 text-accent bg-accent/10"
              data-testid={`driver-delivery-badge-${trip.id}`}
            >
              <ShoppingCart className="w-3 h-3" /> DELIVERY
            </span>
          )}
          <div className="text-3xl font-black mt-1">Nuevo viaje</div>
          <div className="text-sm text-muted-foreground">
            Aceptalo para ver los detalles
          </div>
        </div>
        <Button
          onClick={() => onChange(trip, "accepted")}
          className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold w-full h-16 text-xl"
          data-testid={`accept-trip-${trip.id}`}
        >
          <CheckCircle2 className="w-7 h-7 mr-2" /> Aceptar viaje
        </Button>
      </Card>
    );
  }

  return (
    <Card
      className="bg-card border-l-4 p-4 space-y-3"
      style={{ borderLeftColor: color }}
      data-testid={`driver-trip-${trip.id}`}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <TripStatusPill status={trip.status} />
            {trip.trip_type === "delivery" && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] font-bold border border-accent/60 text-accent bg-accent/10"
                data-testid={`driver-delivery-badge-${trip.id}`}
              >
                <ShoppingCart className="w-3 h-3" /> DELIVERY
              </span>
            )}
          </div>
          <div className="mt-1 text-2xl font-bold flex items-center gap-1.5">
            <User className="w-5 h-5" /> {trip.passenger_name || "Pasajero"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black font-mono flex items-center text-amber-400">
            <DollarSign className="w-6 h-6" />{Number(trip.price || 0).toFixed(2)}
          </div>
          <div className="text-xs font-mono uppercase text-muted-foreground">{trip.payment_method}</div>
          {trip.driver_income > 0 && (
            <div className="text-xs font-mono text-emerald-400 mt-1">Tu %: {fmt(trip.driver_income)}</div>
          )}
        </div>
      </div>

      <div className="space-y-3 text-base">
        {/* For deliveries show products AND then addresses (both optional). */}
        {trip.trip_type === "delivery" && (
          <div className="flex items-start gap-2 bg-accent/5 border border-accent/30 rounded-md p-2">
            <ShoppingCart className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase text-muted-foreground font-mono">Productos</div>
              <div className="break-words whitespace-pre-line text-lg font-bold text-white uppercase" data-testid={`driver-trip-items-${trip.id}`}>
                {trip.items || "—"}
              </div>
            </div>
          </div>
        )}
        {(trip.trip_type !== "delivery" || trip.pickup_address) && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase text-muted-foreground font-mono">
                {trip.trip_type === "delivery" ? "Recoger en" : "Pickup"}
              </div>
              {pickupAlias ? (
                <>
                  <div className="break-words font-bold text-primary">★ {pickupAlias}</div>
                  <div className="text-[11px] text-muted-foreground break-words leading-tight">{trip.pickup_address}</div>
                </>
              ) : (
                <div className="break-words">{pickupShort || "—"}</div>
              )}
            </div>
            {trip.pickup_address && (
              <a
                href={
                  // Prefer address text (often includes the business name
                  // from Google Places, e.g. "Maxx Foods, 4055 23rd Ave SW"),
                  // which lets Google geocode to the exact establishment
                  // instead of dropping the pin on the road midpoint. Fall
                  // back to lat,lng only when we have no address.
                  // When we ALSO know the Google `place_id`, append it as
                  // `destination_place_id` — Maps then resolves to that
                  // exact business/POI even if a duplicate name exists in
                  // the area or the business rebrands its formatted name.
                  (() => {
                    const base = "https://www.google.com/maps/dir/?api=1&destination=";
                    if (trip.pickup_address) {
                      const q = `${base}${encodeURIComponent(trip.pickup_address)}`;
                      return trip.pickup_place_id
                        ? `${q}&destination_place_id=${encodeURIComponent(trip.pickup_place_id)}`
                        : q;
                    }
                    return `${base}${trip.pickup_lat},${trip.pickup_lng}`;
                  })()
                }
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`open-pickup-maps-${trip.id}`}
                className="flex items-center gap-1.5 text-sm text-primary border border-primary/40 rounded-md px-3 py-2 font-bold hover:bg-primary/10 transition-colors flex-shrink-0"
              >
                <Navigation className="w-4 h-4" /> Ir
              </a>
            )}
          </div>
        )}
        {(trip.trip_type !== "delivery" || trip.dropoff_address) && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase text-muted-foreground font-mono">
                {trip.trip_type === "delivery" ? "Entregar en" : "Drop off"}
              </div>
              {dropoffAlias ? (
                <>
                  <div className="break-words font-bold text-accent">★ {dropoffAlias}</div>
                  <div className="text-[11px] text-muted-foreground break-words leading-tight">{trip.dropoff_address}</div>
                </>
              ) : (
                <div className="break-words">{dropoffShort || "—"}</div>
              )}
            </div>
            {trip.dropoff_address && (
              <a
                href={
                  // Same rationale as pickup — plus append `destination_place_id`
                  // when known so Google Maps pins the exact business/POI.
                  (() => {
                    const base = "https://www.google.com/maps/dir/?api=1&destination=";
                    if (trip.dropoff_address) {
                      const q = `${base}${encodeURIComponent(trip.dropoff_address)}`;
                      return trip.dropoff_place_id
                        ? `${q}&destination_place_id=${encodeURIComponent(trip.dropoff_place_id)}`
                        : q;
                    }
                    return `${base}${trip.dropoff_lat},${trip.dropoff_lng}`;
                  })()
                }
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`open-dropoff-maps-${trip.id}`}
                className="flex items-center gap-1.5 text-sm text-accent border border-accent/40 rounded-md px-3 py-2 font-bold hover:bg-accent/10 transition-colors flex-shrink-0"
              >
                <Navigation className="w-4 h-4" /> Ir
              </a>
            )}
          </div>
        )}
      </div>

      {trip.notes && (
        <div
          className="notes-glow relative rounded-xl bg-fuchsia-600 border-2 border-fuchsia-200 px-3 py-3"
          data-testid={`driver-trip-notes-${trip.id}`}
        >
          <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.25em] text-fuchsia-100">
            <AlertTriangle className="w-3.5 h-3.5" /> Notas del dispatcher
          </div>
          <div className="mt-1 text-xl font-black leading-snug text-white uppercase break-words whitespace-pre-line" style={{ fontFamily: "Manrope" }}>
            {trip.notes}
          </div>
        </div>
      )}

      {/* Driver investment — DELIVERIES ONLY (receipt photo for purchases).
          Regular rides never show it so notes + buttons stay visible. Also
          kept for any trip that already has receipts attached. */}
      {(trip.trip_type === "delivery" || (trip.investment_receipts?.length > 0) || Number(trip.driver_investment) > 0)
        && ["accepted", "arrived_pickup", "with_client", "pending_review"].includes(trip.status) && (
        <DriverInvestmentField trip={trip} />
      )}

      {/* Pending review notice */}
      {trip.status === "pending_review" && (
        <div
          className="rounded-md border border-pink-500/50 bg-pink-500/10 p-3"
          data-testid={`pending-review-notice-${trip.id}`}
        >
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full bg-pink-400 animate-pulse mt-1.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-bold text-pink-300">Esperando aprobación del dispatcher</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                El dispatcher está revisando este viaje. Cuando lo apruebe se cerrará y sumará a tus ingresos.
              </div>
            </div>
          </div>
          {trip.payment_method && (
            <div
              className="text-4xl font-mono uppercase text-amber-400 font-bold mt-3 text-center w-full"
              data-testid={`pending-review-payment-${trip.id}`}
            >
              {trip.payment_method}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2 pt-2">
        {trip.status === "assigned" && (
          <Button
            onClick={() => onChange(trip, "accepted")}
            className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold col-span-2 h-14 text-lg"
            data-testid={`accept-trip-${trip.id}`}
          >
            <CheckCircle2 className="w-6 h-6 mr-1" /> Aceptar viaje
          </Button>
        )}
        {trip.status === "accepted" && (
          <Button
            onClick={() => {
              onChange(trip, "arrived_pickup");
              toast.success("Solicitud enviada al dispatcher", {
                description: "El dispatcher va a llamar al cliente por ti.",
              });
            }}
            data-testid={`call-client-${trip.id}`}
            className="bg-orange-500 hover:bg-orange-600 text-black font-bold col-span-2 h-14 text-lg"
          >
            <Phone className="w-6 h-6 mr-2" /> LLAMAR AL CLIENTE <Phone className="w-6 h-6 ml-2" />
          </Button>
        )}
        {trip.status === "arrived_pickup" && (
          <Button
            onClick={() => onChange(trip, "with_client")}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold col-span-2 h-14 text-lg"
            data-testid={`with-client-${trip.id}`}
          >
            Ya tengo al cliente
          </Button>
        )}
        {trip.status === "with_client" && (
          <Button
            onClick={() => onChange(trip, "pending_review")}
            className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold col-span-2 h-14 text-lg border border-zinc-700 shadow-sm"
            data-testid={`finish-trip-${trip.id}`}
          >
            Terminar viaje
          </Button>
        )}
        {isActive && !["with_client", "pending_review"].includes(trip.status) && (
          <Button
            variant="outline"
            onClick={() => onRelease?.(trip)}
            className="border-amber-500 text-amber-400 hover:bg-amber-500/10 col-span-2"
            data-testid={`release-trip-${trip.id}`}
          >
            <Undo2 className="w-4 h-4 mr-1" /> Liberar viaje · Devolver a dispatch
          </Button>
        )}
      </div>
    </Card>
  );
}

function DriverInvestmentField({ trip }) {
  const [uploading, setUploading] = useState(false);
  const [receipts, setReceipts] = useState(() => (
    trip.investment_receipts && trip.investment_receipts.length
      ? trip.investment_receipts
      : (trip.investment_receipt ? [trip.investment_receipt] : [])
  ));
  const fileInputRef = useRef(null);

  const isNonCash = (trip.payment_method || "").toLowerCase() !== "cash";
  const investment = Number(trip.driver_investment || 0);
  const commission = Number(trip.driver_income || 0);
  const owedByLLC = isNonCash ? commission + investment : 0;
  const owedToLLC = isNonCash ? 0 : Math.max(0, Number(trip.price || 0) - commission - investment);

  const uploadReceipt = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" pasa de 10 MB — sáltalo`);
        continue;
      }
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(`/trips/${trip.id}/investment-receipt`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setReceipts(data.receipts || []);
        toast.success(`Recibo subido (${files.length > 1 ? files.indexOf(file) + 1 + "/" + files.length : ""}${file.name})`);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setUploading(false);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const deleteReceipt = async (idx) => {
    if (!window.confirm("¿Eliminar este recibo?")) return;
    try {
      const { data } = await api.delete(`/trips/${trip.id}/investment-receipt/${idx}`);
      setReceipts(data.receipts || []);
      toast.success("Recibo eliminado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3 space-y-2" data-testid={`driver-investment-${trip.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">
          Foto del recibo de inversión
        </div>
        <div className="text-[11px] font-mono text-cyan-300">
          {investment > 0 ? `$${investment.toFixed(2)}` : "—"}
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground">
        Sube una o varias fotos del recibo. El dispatcher ingresa el monto en el sistema.
      </div>

      {receipts.length > 0 && (
        <ul className="space-y-1" data-testid={`receipt-list-${trip.id}`}>
          {receipts.map((r, idx) => (
            <li
              key={r.path}
              className="flex items-center justify-between gap-2 rounded bg-cyan-500/10 border border-cyan-500/20 px-2 py-1"
              data-testid={`receipt-item-${trip.id}-${idx}`}
            >
              <ReceiptViewLink
                receipt={r}
                label={`Recibo ${idx + 1}`}
                testId={`receipt-view-${trip.id}-${idx}`}
              />
              <button
                type="button"
                onClick={() => deleteReceipt(idx)}
                data-testid={`receipt-delete-${trip.id}-${idx}`}
                className="text-[10px] font-mono uppercase tracking-widest text-red-400 hover:text-red-300"
                title="Eliminar este recibo"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="pt-1 space-y-1.5">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          onChange={uploadReceipt}
          disabled={uploading}
          data-testid={`receipt-input-${trip.id}`}
          className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-secondary file:text-white file:font-bold hover:file:bg-secondary/80"
        />
        {uploading && <div className="text-[10px] text-cyan-400">Subiendo…</div>}
      </div>

      {investment > 0 && (
        <div className="text-[11px] font-mono grid grid-cols-2 gap-2 pt-1 border-t border-cyan-500/20">
          {isNonCash ? (
            <div className="col-span-2 text-emerald-400">
              Empresa te reembolsa: <span className="font-bold">${owedByLLC.toFixed(2)}</span>
              <span className="text-muted-foreground text-[10px]"> (comisión ${commission.toFixed(2)} + inversión ${investment.toFixed(2)})</span>
            </div>
          ) : (
            <div className="col-span-2 text-amber-400">
              Entregar a la empresa: <span className="font-bold">${owedToLLC.toFixed(2)}</span>
              <span className="text-muted-foreground text-[10px]"> (cash - comisión - inversión)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReceiptViewLink({ receipt, label, testId }) {
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const isImage = String(receipt.content_type || "").startsWith("image/");

  const open = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/receipts/${receipt.path}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: receipt.content_type });
      const url = URL.createObjectURL(blob);
      if (isImage) {
        // Show inline lightbox. Mobile browsers block async window.open()
        // after an await (no direct user gesture) so tapping the eye would
        // silently do nothing — the modal sidesteps that.
        setPreviewUrl(url);
      } else {
        // Fallback for PDFs / non-images: force a download.
        const a = document.createElement("a");
        a.href = url;
        a.download = receipt.original_filename || "recibo";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={busy}
        data-testid={testId}
        className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
      >
        👁 {label || "Ver recibo"}
      </button>
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={close}
          data-testid={`${testId}-preview`}
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-3 right-3 text-white text-2xl w-10 h-10 flex items-center justify-center rounded-full bg-black/60 border border-white/20"
            aria-label="Cerrar recibo"
            data-testid={`${testId}-close`}
          >
            ✕
          </button>
          <img
            src={previewUrl}
            alt={label || "Recibo"}
            className="max-h-[90vh] max-w-full object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
